import {
  Button,
  FilterDefs,
  GridSortConfig,
  GridTableLayout,
  PageHeaderLayout,
  toggleFilter,
  useGridTableLayoutState,
} from "@homebound/beam";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  Maybe,
  Order,
  ProductOfferingOrder,
  ProductOfferingsFilter,
  ReadyPlanStatus,
} from "src/generated/graphqlTypes";
import { useProjectCommunityFilter, useReadyPlanStatusFilter } from "src/hooks";
import {
  GlobalProductOfferingsPageQuery,
  useGlobalProductOfferingsPageQuery,
} from "src/routes/libraries/product-offerings/GlobalProductOfferings.generated";
import {
  createGlobalProductOfferingsColumns,
  createGlobalProductOfferingsRows,
  createGlobalProductOfferingsRowStyles,
} from "src/routes/libraries/product-offerings/GlobalProductOfferingsTable";
import { addEntityParam, createProductOfferingUrl } from "src/RouteUrls";
import { parseOrder, toOrder } from "src/utils/ordering";

export function GlobalProductOfferings() {
  const navigate = useNavigate();
  const { projectCommunityFilter } = useProjectCommunityFilter();
  const { rpStatusFilter: status } = useReadyPlanStatusFilter({ omitArchived: true });
  const filterDefs: FilterDefs<ProductOfferingsFilterWithArchive> = useMemo(
    () => ({ status, project: projectCommunityFilter, archived: toggleFilter({ label: "Show Archived" }) }),
    [projectCommunityFilter, status],
  );
  const layoutState = useGridTableLayoutState<ProductOfferingsFilterWithArchive>({
    persistedFilter: { filterDefs, storageKey: "global-product-offering-filter" },
    search: "server",
  });
  const [orderBy, setOrderBy] = useState<ProductOfferingOrder>(defaultGlobalProductOfferingsOrder);
  const query = useGlobalProductOfferingsPageQuery({
    variables: {
      filter: mapToFilter(layoutState.filter, layoutState.searchString),
      orderBy,
      page: { offset: 0, limit: PAGE_SIZE },
    },
  });
  const sorting: GridSortConfig = useMemo(
    () => ({
      on: "server",
      onSort: (key, direction) => setOrderBy(toOrder(key, direction)),
      value: parseOrder(orderBy),
    }),
    [orderBy],
  );
  const columns = useMemo(() => createGlobalProductOfferingsColumns(), []);
  const rowStyles = useMemo(() => createGlobalProductOfferingsRowStyles(), []);
  const createRows = useCallback(
    (data: GlobalProductOfferingsPageQuery | undefined) =>
      createGlobalProductOfferingsRows(data?.productOfferings.entities ?? []),
    [],
  );
  const maybeFetchNextPage = useCallback(async () => {
    if (query.data?.productOfferings.pageInfo.hasNextPage) {
      await query.fetchMore({
        variables: { page: { offset: query.data.productOfferings.entities.length, limit: PAGE_SIZE } },
      });
    }
  }, [query]);

  return (
    <PageHeaderLayout
      pageHeader={{
        title: "Product Offerings",
        rightSlot: <Button label="Create New" onClick={() => navigate(createProductOfferingUrl(addEntityParam))} />,
      }}
    >
      <GridTableLayout
        defaultView="card"
        layoutState={layoutState}
        withCardView
        tableProps={{
          query,
          createRows,
          columns,
          sorting,
          rowStyles,
          as: "virtual",
          emptyFallback: "No Product Offerings Found",
          infiniteScroll: { onEndReached: maybeFetchNextPage },
        }}
      />
    </PageHeaderLayout>
  );
}

type ProductOfferingsFilterWithArchive = ProductOfferingsFilter & { archived?: Maybe<boolean> };

export const defaultGlobalProductOfferingsFilter: ProductOfferingsFilter = {
  effectiveStatus: [ReadyPlanStatus.Active, ReadyPlanStatus.Draft],
};

function mapToFilter(filter: ProductOfferingsFilterWithArchive, searchString?: string): ProductOfferingsFilter {
  const { archived, status, ...rest } = filter;

  return {
    ...rest,
    ...(status?.nonEmpty ? { effectiveStatus: status } : archived ? {} : defaultGlobalProductOfferingsFilter),
    ...(searchString && { search: searchString }),
  };
}

const PAGE_SIZE = 40;
export const defaultGlobalProductOfferingsPage = { offset: 0, limit: PAGE_SIZE };
export const defaultGlobalProductOfferingsOrder: ProductOfferingOrder = { name: Order.Asc };
